"use client";

import { FC, useState } from "react";

import { Box, Button, Dialog, IconButton } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useForm } from "react-hook-form";

import { FieldController } from "../forms/FieldController";
import { passwordPattern, passwordRuleMessage } from "../../utils/constants";
import { resetMyPassword } from "../../actions/userActions";
import { updateUserPasswordCompliance } from "../../actions/userAttributeActions";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { signOut } from "next-auth/react";

type PasswordUpdateRequiredDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

interface FormUpdatePassword {
  currentPassword: string;
  password: string;
  confirmPassword: string;
}

export const PasswordUpdateRequiredDialog: FC<
  PasswordUpdateRequiredDialogProps
> = ({ open, setOpen }) => {
  const [showCurrentPassword, setShowCurrentPassword] =
    useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [passwordUpdated, setPasswordUpdated] = useState<boolean>(false);
  const [passwordNotUpdated, setPasswordNotUpdated] = useState<boolean>(false);

  const {
    handleSubmit,
    control,
    formState: { isValid },
    getValues,
  } = useForm<FormUpdatePassword>({
    mode: "all",
    defaultValues: {},
  });

  const handleLogout = () => {
    const contextParam = new URLSearchParams(window.location.search).get("context");
    const callbackUrl = contextParam ? `/login?context=${contextParam}` : "/login";
    signOut({ callbackUrl });
  };

  const onSubmit = async () => {
    const res = await resetMyPassword(
      getValues("currentPassword"),
      getValues("password"),
    );

    if (res.status === 204) {
      await updateUserPasswordCompliance(true);
      setPasswordUpdated(true);
      setOpen(false);

      setTimeout(() => {
        const contextParam = new URLSearchParams(window.location.search).get("context");
        const callbackUrl = contextParam ? `/login?context=${contextParam}` : "/login";
        signOut({ callbackUrl });
      }, 2000);
    } else if (res.status === 401) {
      setPasswordNotUpdated(true);
    } else {
      setPasswordNotUpdated(true);
    }
  };

  const handleClickShowCurrentPassword = () =>
    setShowCurrentPassword((show) => !show);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);

  return (
    <>
      <Dialog open={open} onClose={() => {}} disableEscapeKeyDown>
        <DialogTitle
          id="password-update-required-dialog-title"
          sx={{ textAlign: "center", m: 2 }}
        >
          Password Update Required
        </DialogTitle>
        <DialogContent sx={{ width: "500px" }}>
          <Box sx={{ mb: 2 }}>
            <DialogContentText id="password-update-required-description">
              For security reasons, we have updated our password policy. Your
              current password does not meet the new requirements.
            </DialogContentText>
            <DialogContentText sx={{ mt: 2, color: "text.secondary" }}>
              Please update your password to continue using Arbogen. Your new
              password must contain at least 12 characters with at least 1
              uppercase letter, 1 lowercase letter, 1 number and 1 special
              character.
            </DialogContentText>
          </Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <FieldController
              control={control}
              sx={{ height: "112px" }}
              rules={{
                required: "Current password is required.",
              }}
              name="currentPassword"
              label="Current Password"
              type={showCurrentPassword ? "text" : "password"}
              id="currentPassword"
              autoComplete="current-password"
              helperText="Enter your current password."
              formHelperTextProps={{ sx: { ml: 0 } }}
              inputProps={{
                endAdornment: (
                  <IconButton
                    aria-label="toggle current password visibility"
                    onClick={handleClickShowCurrentPassword}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
            <FieldController
              control={control}
              sx={{ height: "112px" }}
              rules={{
                required: "Password is required.",
                pattern: {
                  value: passwordPattern,
                  message: passwordRuleMessage,
                },
              }}
              name="password"
              label="New Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="new-password"
              helperText="Must contain at least 12 characters with at least 1 uppercase letter, 1 lowercase letter, 1 number and 1 special character (@$!%*#?&.,)."
              formHelperTextProps={{ sx: { ml: 0 } }}
              inputProps={{
                endAdornment: (
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
            <FieldController
              control={control}
              sx={{ height: "112px" }}
              rules={{
                required: "Password confirmation is required.",
                validate: (value: string) =>
                  value === getValues("password") ||
                  "The passwords do not match",
              }}
              name="passwordConfirmation"
              label="Confirm New Password"
              type={showConfirmPassword ? "text" : "password"}
              id="passwordConfirmation"
              autoComplete="new-password"
              helperText="Re-enter your new password."
              formHelperTextProps={{ sx: { ml: 0 } }}
              inputProps={{
                endAdornment: (
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowConfirmPassword}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <Button onClick={handleLogout} color="error">
            Logout
          </Button>
          <Button
            variant="contained"
            disabled={!isValid}
            onClick={handleSubmit(onSubmit)}
          >
            Update Password
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={passwordUpdated}
        setOpen={setPasswordUpdated}
        type="success"
        title="Password updated successfully"
        content="Your password has been updated and now meets our security requirements. You will be logged out in a moment. Please log in again with your new password."
      />
      <ConfirmationDialog
        open={passwordNotUpdated}
        setOpen={setPasswordNotUpdated}
        type="error"
        title="Password update failed"
        content="Your password could not be updated. Please verify your current password is correct and that your new password meets all requirements."
      />
    </>
  );
};
